import { Box, Typography, Paper, IconButton, LinearProgress, CircularProgress, useTheme } from '@mui/material';
import { useFormContext, useFieldArray } from 'react-hook-form';
import { FiMic, FiTrash2, FiPlay, FiPause } from 'react-icons/fi';
import Button from '@/components/Common/Button';
import Input from '@/components/Common/Input';
import {
  useCreateOrUpdateAgencyOnboardingForm,
  useDeleteOnboardingFile,
  useUpdatePublicOnboardingForm
} from '@/apis/agency/businessOnboarding/hooks';
import Cookies from 'js-cookie';
import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import { ScrollToTop } from '@/App';
import RecordingOnIcon from '@/assets/icons/RecordingOnIcon';
import { registerOfficeOpsNav } from '../components/officeOpsNav';

type SpeechRecognitionLike = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((event: any) => void) | null;
  onerror: ((event: any) => void) | null;
  onend: (() => void) | null;
};

const getSpeechRecognition = (): (new () => SpeechRecognitionLike) | null => {
  const w = window as any;
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
};

const formatDuration = (seconds: number) => {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
};

const OFFICE_OPS_TRANSCRIPT_PLACEHOLDERS_FALLBACK =
  'Share as much detail as you can — examples, edge cases, and exact phrases all help.';

const OFFICE_OPS_TRANSCRIPT_PLACEHOLDERS: string[] = [
  'e.g: New patients: we take name, date of birth and insurance, then book a 45-min first visit. Existing patients: we confirm DOB and send them to their usual provider.',
  "e.g: We ask what service the customer needs, their preferred date and time, and whether they're a new or existing customer. We check availability, explain relevant services, and confirm the appointment.",
  OFFICE_OPS_TRANSCRIPT_PLACEHOLDERS_FALLBACK,
  "e.g: We don't approve refunds, special pricing, billing adjustments, or exceptions without authorization. We collect the details and forward the request to the designated person for confirmation.",
  'e.g: We listen patiently to upset customers, acknowledge their concerns, and explain available solutions calmly. If a complaint involves a billing dispute, repeated issue, or aggressive behaviour, we escalate it to the appropriate team member.'
];

export default function OfficeOperationsForm() {
  const theme = useTheme();
  const currentFormId = Cookies.get('onboarding_id');
  const { control, watch, setValue, getValues } = useFormContext();
  const isBusinessOnboardingStage2 = location.pathname.includes('/business-onboarding/stage-2');

  const { mutate: updateAgency, isPending: isAgencyUpdating } = useCreateOrUpdateAgencyOnboardingForm(false);
  const { mutate: updatePublic, isPending: isPublicUpdating } = useUpdatePublicOnboardingForm();
  const { deleteFile } = useDeleteOnboardingFile();

  const isUploading = isAgencyUpdating || isPublicUpdating;

  useEffect(() => {
    setValue('isUploadingDoc', isUploading);
  }, [isUploading, setValue]);

  const updateOnboardingForm = useMemo(
    () =>
      isBusinessOnboardingStage2
        ? (payload: FormData, options?: any) =>
            currentFormId && updatePublic({ formId: currentFormId, payload }, options)
        : (payload: FormData, options?: any) => updateAgency(payload, options),
    [isBusinessOnboardingStage2, currentFormId, updatePublic, updateAgency]
  );

  useFieldArray({
    control,
    name: 'office_operations_files_attributes'
  });

  const [questionIndex, setQuestionIndex] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [micError, setMicError] = useState<string | null>(null);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [localAudioUrls, setLocalAudioUrls] = useState<Record<number, string>>({});
  const [isPlaying, setIsPlaying] = useState(false);
  const [speechSupported] = useState(() => Boolean(getSpeechRecognition()));
  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const baseTranscriptRef = useRef('');
  const shouldContinueRecognitionRef = useRef(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  /** Index frozen when a recording starts — used by STT / MediaRecorder callbacks so nav can't reassign them. */
  const recordingIndexRef = useRef<number | null>(null);
  const recordingSecondsRef = useRef(0);
  const isRecordingRef = useRef(false);

  isRecordingRef.current = isRecording;

  const answers = watch('office_operations_files_attributes') || [];
  const totalQuestions = answers.length;
  const answeredCount = answers.filter((a: any) => a?.transcript?.trim()).length;
  const progressPct = totalQuestions ? Math.round((answeredCount / totalQuestions) * 100) : 0;

  const currentAnswer = answers[questionIndex] || {};
  const localAudioUrl = localAudioUrls[questionIndex] || null;
  const hasRecording = Boolean(currentAnswer?.file_url || localAudioUrl || currentAnswer?.file instanceof Blob);
  const playbackUrl = localAudioUrl || currentAnswer?.file_url || null;
  const displayDuration = isRecording ? recordingSeconds : (currentAnswer?.duration_seconds ?? 0);
  const isCurrentUploading = uploadingIndex === questionIndex;

  const stopMediaTracks = () => {
    mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
    mediaStreamRef.current = null;
  };

  const clearTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const stopRecognition = () => {
    shouldContinueRecognitionRef.current = false;
    const recognition = recognitionRef.current;
    if (recognition) {
      // Drop handlers so late events can't write after stop / question change
      recognition.onresult = null;
      recognition.onerror = null;
      recognition.onend = null;
      try {
        recognition.abort();
      } catch {
        try {
          recognition.stop();
        } catch {
          /* ignore */
        }
      }
    }
    recognitionRef.current = null;
  };

  const ensureTranscriptString = useCallback(
    (index: number) => {
      const current = getValues(`office_operations_files_attributes.${index}.transcript`);
      if (current == null) {
        setValue(`office_operations_files_attributes.${index}.transcript`, '');
      }
    },
    [getValues, setValue]
  );

  const uploadRecording = useCallback(
    (index: number, file: File, transcript: string) => {
      const answerId = getValues(`office_operations_files_attributes.${index}.id`);
      const question = getValues(`office_operations_files_attributes.${index}.question`) || '';
      const section = getValues(`office_operations_files_attributes.${index}.section`) || '';

      setValue(`office_operations_files_attributes.${index}.file_name`, file.name);
      setValue(`office_operations_files_attributes.${index}.transcript`, transcript || '', { shouldDirty: true });
      setValue(`office_operations_files_attributes.${index}.question`, question);

      const fd = new FormData();
      if (answerId) {
        fd.append('onboarding_form[office_operations_files_attributes][][id]', String(answerId));
      }
      fd.append('onboarding_form[office_operations_files_attributes][][file]', file);
      fd.append('onboarding_form[office_operations_files_attributes][][question]', question);
      fd.append('onboarding_form[office_operations_files_attributes][][transcript]', transcript || '');
      if (section) {
        fd.append('onboarding_form[office_operations_files_attributes][][section]', section);
      }

      setUploadingIndex(index);
      updateOnboardingForm(fd, {
        onSuccess: (data: any) => {
          const uploadedList = data?.office_operations_files_attributes || [];
          const uploaded =
            (answerId != null && uploadedList.find((a: any) => String(a?.id) === String(answerId))) ||
            uploadedList.find((a: any) => a?.question === question) ||
            uploadedList[index];
          if (uploaded) {
            setValue(`office_operations_files_attributes.${index}.id`, uploaded.id);
            setValue(`office_operations_files_attributes.${index}.file_url`, uploaded.file_url);
            setValue(`office_operations_files_attributes.${index}.file`, uploaded.file);
            if (uploaded.transcript != null) {
              setValue(`office_operations_files_attributes.${index}.transcript`, uploaded.transcript || '');
            }
          }
        },
        onSettled: () => {
          setUploadingIndex((current) => (current === index ? null : current));
        }
      });
    },
    [getValues, setValue, updateOnboardingForm]
  );

  const persistTranscriptOnly = useCallback(
    (index: number) => {
      const transcript = (getValues(`office_operations_files_attributes.${index}.transcript`) || '').trim();
      const answerId = getValues(`office_operations_files_attributes.${index}.id`);
      const question = getValues(`office_operations_files_attributes.${index}.question`) || '';
      const section = getValues(`office_operations_files_attributes.${index}.section`) || '';
      const hasFile = Boolean(
        getValues(`office_operations_files_attributes.${index}.file_url`) ||
          getValues(`office_operations_files_attributes.${index}.file`)
      );

      // Only auto-save typed answers that aren't already covered by a file upload path
      if (!transcript || hasFile) return;

      const fd = new FormData();
      if (answerId) {
        fd.append('onboarding_form[office_operations_files_attributes][][id]', String(answerId));
      }
      fd.append('onboarding_form[office_operations_files_attributes][][question]', question);
      fd.append('onboarding_form[office_operations_files_attributes][][transcript]', transcript);
      if (section) {
        fd.append('onboarding_form[office_operations_files_attributes][][section]', section);
      }

      setUploadingIndex(index);
      updateOnboardingForm(fd, {
        onSuccess: (data: any) => {
          const uploadedList = data?.office_operations_files_attributes || [];
          const uploaded =
            (answerId != null && uploadedList.find((a: any) => String(a?.id) === String(answerId))) ||
            uploadedList.find((a: any) => a?.question === question) ||
            uploadedList[index];
          if (uploaded?.id != null) {
            setValue(`office_operations_files_attributes.${index}.id`, uploaded.id);
            if (uploaded.transcript != null) {
              setValue(`office_operations_files_attributes.${index}.transcript`, uploaded.transcript || '');
            }
          }
        },
        onSettled: () => {
          setUploadingIndex((current) => (current === index ? null : current));
        }
      });
    },
    [getValues, setValue, updateOnboardingForm]
  );

  const startSpeechRecognition = (lockedIndex: number) => {
    const RecognitionCtor = getSpeechRecognition();
    if (!RecognitionCtor) return;

    // Must start inside the user-gesture stack (before any await)
    stopRecognition();
    const recognition = new RecognitionCtor();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    shouldContinueRecognitionRef.current = true;

    recognition.onresult = (event: any) => {
      // Ignore late events after stop or if this session was superseded
      if (!shouldContinueRecognitionRef.current || recordingIndexRef.current !== lockedIndex) return;

      let interim = '';
      let finalChunk = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const text = result[0]?.transcript || '';
        if (result.isFinal) {
          finalChunk += text;
        } else {
          interim += text;
        }
      }

      if (finalChunk) {
        baseTranscriptRef.current = [baseTranscriptRef.current, finalChunk.trim()].filter(Boolean).join(' ').trim();
      }

      const display = [baseTranscriptRef.current, interim.trim()].filter(Boolean).join(' ').trim();
      setValue(`office_operations_files_attributes.${lockedIndex}.transcript`, display, {
        shouldDirty: true
      });
    };

    recognition.onerror = () => {
      /* keep recording audio even if STT fails */
    };

    recognition.onend = () => {
      if (shouldContinueRecognitionRef.current && recordingIndexRef.current === lockedIndex) {
        try {
          recognition.start();
        } catch {
          /* ignore restart errors */
        }
      }
    };

    recognitionRef.current = recognition;
    try {
      recognition.start();
    } catch {
      /* ignore — audio recording can still proceed */
    }
  };

  const getMicErrorMessage = (err: unknown) => {
    if (!window.isSecureContext) {
      return 'Microphone needs a secure origin. Use https:// or http://localhost (not a LAN IP like http://192.168.x.x).';
    }
    const name = err && typeof err === 'object' && 'name' in err ? String((err as any).name) : '';
    if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
      return 'Microphone permission was blocked. Allow mic access for this site and try again.';
    }
    if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
      return 'No microphone was found. Connect a mic and try again.';
    }
    if (name === 'NotReadableError' || name === 'TrackStartError') {
      return 'Microphone is busy (another app may be using it). Close other apps and try again.';
    }
    if (err instanceof Error && err.message) return err.message;
    return 'Unable to access the microphone. Please allow mic access and try again.';
  };

  const startRecording = async () => {
    if (isRecordingRef.current || isStarting) return;

    const lockedIndex = questionIndex;
    recordingIndexRef.current = lockedIndex;

    setMicError(null);
    setIsStarting(true);

    // Flip UI immediately so the click always feels responsive
    setIsRecording(true);
    isRecordingRef.current = true;
    setRecordingSeconds(0);
    recordingSecondsRef.current = 0;

    const existingTranscript = getValues(`office_operations_files_attributes.${lockedIndex}.transcript`) || '';
    baseTranscriptRef.current = existingTranscript.trim();

    // SpeechRecognition must be started in the same synchronous click turn
    startSpeechRecognition(lockedIndex);

    clearTimer();
    timerRef.current = setInterval(() => {
      setRecordingSeconds((s) => {
        const next = s + 1;
        recordingSecondsRef.current = next;
        return next;
      });
    }, 1000);

    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error('Microphone recording is not supported in this browser.');
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Cancelled while awaiting permission, or a newer session started for another question
      if (!isRecordingRef.current || recordingIndexRef.current !== lockedIndex) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }

      mediaStreamRef.current = stream;
      chunksRef.current = [];

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : MediaRecorder.isTypeSupported('audio/mp4')
            ? 'audio/mp4'
            : '';

      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const idx = lockedIndex;
        const blobType = recorder.mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type: blobType });
        const ext = blobType.includes('mp4') ? 'mp4' : 'webm';
        const file = new File([blob], `office-operations-q${idx + 1}.${ext}`, { type: blobType });
        const url = URL.createObjectURL(blob);
        setLocalAudioUrls((prev) => {
          if (prev[idx]) URL.revokeObjectURL(prev[idx]);
          return { ...prev, [idx]: url };
        });

        setValue(`office_operations_files_attributes.${idx}.duration_seconds`, recordingSecondsRef.current, {
          shouldDirty: true
        });

        const transcript = getValues(`office_operations_files_attributes.${idx}.transcript`) || '';
        uploadRecording(idx, file, transcript);
        stopMediaTracks();
        if (recordingIndexRef.current === lockedIndex) {
          recordingIndexRef.current = null;
        }
      };

      recorder.start(250);
      setIsStarting(false);
    } catch (err) {
      shouldContinueRecognitionRef.current = false;
      stopRecognition();
      clearTimer();
      stopMediaTracks();
      setIsRecording(false);
      isRecordingRef.current = false;
      setIsStarting(false);
      if (recordingIndexRef.current === lockedIndex) {
        recordingIndexRef.current = null;
      }
      setMicError(getMicErrorMessage(err));
    }
  };

  const stopRecording = () => {
    shouldContinueRecognitionRef.current = false;
    stopRecognition();
    clearTimer();
    setIsRecording(false);
    isRecordingRef.current = false;
    setIsStarting(false);

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    } else {
      stopMediaTracks();
      recordingIndexRef.current = null;
    }
    mediaRecorderRef.current = null;
  };

  const handleToggleRecording = (event?: MouseEvent) => {
    event?.preventDefault();
    event?.stopPropagation();
    if (isRecordingRef.current) {
      stopRecording();
    } else {
      void startRecording();
    }
  };

  const clearLocalAudio = (index: number) => {
    setLocalAudioUrls((prev) => {
      if (prev[index]) URL.revokeObjectURL(prev[index]);
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const handleDeleteRecording = () => {
    if (isRecording) stopRecording();
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
    clearLocalAudio(questionIndex);

    const answerId = getValues(`office_operations_files_attributes.${questionIndex}.id`);
    setValue(`office_operations_files_attributes.${questionIndex}.file`, null);
    setValue(`office_operations_files_attributes.${questionIndex}.file_url`, '');
    setValue(`office_operations_files_attributes.${questionIndex}.file_name`, '');
    setValue(`office_operations_files_attributes.${questionIndex}.duration_seconds`, 0);
    setValue(`office_operations_files_attributes.${questionIndex}.transcript`, '', { shouldDirty: true });
    baseTranscriptRef.current = '';
    setRecordingSeconds(0);

    if (answerId) {
      deleteFile(answerId, 'office_operations_files_attributes');
      setValue(`office_operations_files_attributes.${questionIndex}.id`, undefined);
    }
  };

  const handleReRecord = (event?: MouseEvent) => {
    event?.preventDefault();
    event?.stopPropagation();
    setMicError(null);
    clearLocalAudio(questionIndex);
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
    setValue(`office_operations_files_attributes.${questionIndex}.file`, null);
    setValue(`office_operations_files_attributes.${questionIndex}.file_url`, '');
    setValue(`office_operations_files_attributes.${questionIndex}.file_name`, '');
    setValue(`office_operations_files_attributes.${questionIndex}.duration_seconds`, 0);
    setValue(`office_operations_files_attributes.${questionIndex}.transcript`, '', { shouldDirty: true });
    baseTranscriptRef.current = '';
    setRecordingSeconds(0);
    void startRecording();
  };

  const togglePlayback = (event?: MouseEvent) => {
    event?.preventDefault();
    event?.stopPropagation();
    if (!playbackUrl) return;
    if (!audioRef.current) {
      audioRef.current = new Audio(playbackUrl);
      audioRef.current.onended = () => setIsPlaying(false);
    }
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.src = playbackUrl;
      void audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const resetUiForQuestionChange = () => {
    if (isRecordingRef.current) {
      stopRecording();
    }
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
    baseTranscriptRef.current = '';
    setRecordingSeconds(0);
    setMicError(null);
  };

  const goPrev = () => {
    if (questionIndex <= 0 || isRecording) return false;
    persistTranscriptOnly(questionIndex);
    resetUiForQuestionChange();
    const nextIndex = questionIndex - 1;
    ensureTranscriptString(nextIndex);
    setQuestionIndex(nextIndex);
    return true;
  };

  const goNext = () => {
    if (questionIndex >= totalQuestions - 1 || isRecording) return false;
    persistTranscriptOnly(questionIndex);
    resetUiForQuestionChange();
    const nextIndex = questionIndex + 1;
    ensureTranscriptString(nextIndex);
    setQuestionIndex(nextIndex);
    return true;
  };

  const officeOpsNavRef = useRef({ goPrev, goNext, questionIndex, totalQuestions, isRecording });
  officeOpsNavRef.current = { goPrev, goNext, questionIndex, totalQuestions, isRecording };

  useEffect(() => {
    registerOfficeOpsNav({
      goPrev: () => officeOpsNavRef.current.goPrev(),
      goNext: () => officeOpsNavRef.current.goNext(),
      canGoPrev: () => officeOpsNavRef.current.questionIndex > 0 && !officeOpsNavRef.current.isRecording,
      canGoNext: () =>
        officeOpsNavRef.current.questionIndex < officeOpsNavRef.current.totalQuestions - 1 &&
        !officeOpsNavRef.current.isRecording
    });
  }, [questionIndex, totalQuestions, isRecording]);

  useEffect(() => {
    return () => registerOfficeOpsNav(null);
  }, []);

  // Normalize empty transcripts so the controlled Input never receives undefined
  useEffect(() => {
    for (let index = 0; index < totalQuestions; index++) {
      const transcript = getValues(`office_operations_files_attributes.${index}.transcript`);
      if (transcript == null) {
        setValue(`office_operations_files_attributes.${index}.transcript`, '');
      }
    }
  }, [totalQuestions, getValues, setValue]);

  // Pause playback / reset timer UI when changing questions (recording is stopped in goPrev/goNext first)
  useEffect(() => {
    ensureTranscriptString(questionIndex);
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
    setRecordingSeconds(0);
    baseTranscriptRef.current = '';
  }, [questionIndex, ensureTranscriptString]);

  useEffect(() => {
    return () => {
      shouldContinueRecognitionRef.current = false;
      stopRecognition();
      clearTimer();
      stopMediaTracks();
      Object.values(localAudioUrls).forEach((url) => URL.revokeObjectURL(url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Paper sx={{ border: '1px solid #EAEAEA', borderRadius: 2, p: { xs: 2, md: 3 } }}>
      <ScrollToTop state={questionIndex} />
      <Typography fontWeight={600} fontSize={20} mb={1}>
        Office Operations
      </Typography>
      <Typography variant='body2' color='text.secondary' mb={3}>
        Tell us how your front desk really runs. Record your answers, it&apos;s faster and captures the nuance better
        than typing.
      </Typography>

      <Box mb={3}>
        <Box display='flex' justifyContent='space-between' alignItems='center' mb={0.75}>
          <Typography variant='body2' color='text.secondary'>
            {answeredCount} of {totalQuestions} answered
          </Typography>
          <Typography variant='body2' color='primary' fontWeight={600}>
            {progressPct}%
          </Typography>
        </Box>
        <LinearProgress
          variant='determinate'
          value={progressPct}
          sx={{
            height: 6,
            borderRadius: 3,
            bgcolor: theme.palette.primary.light,
            '& .MuiLinearProgress-bar': { borderRadius: 3 }
          }}
        />
      </Box>

      <Paper variant='outlined' sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
        <Box display='flex' alignItems='center' mb={1.5} flexWrap='wrap' gap={1}>
          <Typography variant='caption' color='text.secondary' fontWeight={600} letterSpacing={0.6}>
            {currentAnswer?.section}
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            Question {questionIndex + 1} of {totalQuestions}
          </Typography>
        </Box>

        <Typography fontWeight={500} fontSize={16} mb={3} lineHeight={1.5}>
          {currentAnswer?.question}
        </Typography>

        {/* Idle / Recording prompt */}
        {!hasRecording || isRecording ? (
          <Box
            borderRadius={2}
            border='1px solid #ececec'
            py={4}
            px={2}
            display='flex'
            flexDirection='column'
            alignItems='center'
            textAlign='center'
            mb={3}
          >
            <IconButton
              type='button'
              onClick={handleToggleRecording}
              disabled={isCurrentUploading || (isStarting && !isRecording)}
              aria-label={isRecording ? 'Stop recording' : 'Start recording'}
              sx={{
                width: 64,
                height: 64,
                bgcolor: isRecording ? '' : theme.palette.primary.main,
                color: '#fff',
                '&:hover': {
                  bgcolor: isRecording ? '' : theme.palette.primary.dark
                },
                mb: 1.5
              }}
            >
              {isRecording ? <RecordingOnIcon /> : <FiMic size={20} />}
            </IconButton>
            {isRecording ? (
              <>
                <Typography fontWeight={600}>
                  {isStarting ? 'Starting mic…' : `Recording... ${formatDuration(recordingSeconds)}`}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Tap to stop.
                </Typography>
              </>
            ) : (
              <>
                <Typography fontWeight={600}>Tap to record your answer</Typography>
                <Typography variant='body2' color='text.secondary'>
                  We&apos;ll transcribe it automatically — or type below.
                </Typography>
                {!speechSupported && (
                  <Typography variant='caption' color='warning.main' mt={1} display='block'>
                    Live transcription isn&apos;t supported in this browser — you can still record and type your answer.
                  </Typography>
                )}
                {micError && (
                  <Typography variant='caption' color='error' mt={1} display='block' px={2}>
                    {micError}
                  </Typography>
                )}
              </>
            )}
          </Box>
        ) : (
          <Box
            bgcolor='#f8f8f8'
            border='1px solid #ececec'
            borderRadius={2}
            py={2}
            px={2}
            display='flex'
            alignItems='center'
            justifyContent='space-between'
            gap={2}
            mb={3}
            flexWrap='wrap'
          >
            <Box display='flex' alignItems='center' gap={1.5}>
              <IconButton
                type='button'
                onClick={togglePlayback}
                aria-label={isPlaying ? 'Pause playback' : 'Play recording'}
                sx={{
                  width: 44,
                  height: 44,
                  bgcolor: theme.palette.primary.main,
                  color: '#fff',
                  '&:hover': { bgcolor: theme.palette.primary.dark }
                }}
              >
                {isPlaying ? <FiPause size={18} /> : <FiPlay size={18} />}
              </IconButton>
              <Box>
                <Typography fontWeight={600}>Voice answer</Typography>
                <Typography variant='body2' color='text.secondary'>
                  {formatDuration(displayDuration || 0)} recorded
                  {isCurrentUploading ? ' · Uploading…' : ''}
                </Typography>
              </Box>
            </Box>
            <Box display='flex' alignItems='center' gap={1}>
              {isCurrentUploading && <CircularProgress size={18} />}
              <Button
                type='button'
                variant='outlined'
                size='small'
                startIcon={<FiMic />}
                onClick={handleReRecord}
                disabled={isCurrentUploading}
                sx={{ borderRadius: 20, minHeight: 32, bgcolor: '#fff' }}
              >
                Re-record
              </Button>
              <IconButton
                type='button'
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDeleteRecording();
                }}
                sx={{ color: 'error.main' }}
                aria-label='Delete recording'
              >
                <FiTrash2 />
              </IconButton>
            </Box>
          </Box>
        )}

        <Input
          key={`office-ops-transcript-${questionIndex}`}
          name={`office_operations_files_attributes.${questionIndex}.transcript`}
          control={control}
          label={
            isRecording ? 'Listening... transcript will appear as you speak' : 'Type your answer or edit the transcript'
          }
          placeholder={OFFICE_OPS_TRANSCRIPT_PLACEHOLDERS[questionIndex] ?? OFFICE_OPS_TRANSCRIPT_PLACEHOLDERS_FALLBACK}
          multiline
          fullWidth
          minRows={5}
          maxRows={12}
          InputLabelProps={{ shrink: true }}
        />
      </Paper>
    </Paper>
  );
}
